// raceVideo.js — turns a race chart into an MP4 in the browser, ready to
// post on X, TikTok, Reels or YouTube: a title card, the race drawn frame by
// frame (the same maths as the on-screen chart, from raceGeometry.js) and a
// closing card with the final standings, branded with the site and @pl_gami
// throughout. Frames are drawn on a <canvas>, encoded to H.264 by WebCodecs
// and packed into MP4 by Mediabunny, which is only downloaded when someone
// makes a video. No server is involved.
//
// Crests come from /crests/ (scripts/mirror-crests.js): a cross-origin image
// would taint the canvas and the encoder would refuse its frames. A club
// without a local crest gets its initials instead.

import { BRAND } from "../../theme";
import { LOGO_CELLS } from "../AppHeader";
import { clubInfo, seasonText } from "./data";
import {
  boardAt,
  buildSeries,
  fmtDay,
  linePath,
  placeHeads,
  playSeconds,
  raceWindow,
  scales,
  seasonsIn,
  targetDomain,
  xTicksFor,
  yTicksFor,
} from "./raceGeometry";

export const FPS = 30; // X's API takes at most 40
const BITRATE = 5e6;
const T_TITLE = 2.5; // title card; crossfades into the chart over its last FADE
const T_LEAD = 0.6; // the first match day on screen before the lines move
const T_HOLD = 1.5; // the finished chart before the closing card
const T_END = 4; // closing card
const FADE = 0.5;
const MAX_RACE_SECONDS = 120; // keeps every video inside X's 2:20 limit
const DOMAIN_EASE = 1 - 0.88 ** (60 / FPS); // the on-screen chart eases 0.12 per 60 fps frame
const ROW_EASE = 1 - Math.exp(-1 / FPS / 0.09); // leaderboard rows settle in about 300 ms

const FONT = "Poppins, Helvetica, Arial, sans-serif";
const SITE = "premierleaguescorigami.com";
const HANDLE = "@pl_gami";
const X_LOGO = "M18.9 2H21l-6.5 7.4L22 22h-7.1l-4.6-6.2L4.1 22H2l7-8-6.7-12H9l4 6.1L18.9 2z"; // 24×24, as in AppHeader
const MUTED = BRAND.textSecondary;

export const VIDEO_FORMATS = {
  landscape: { width: 1920, height: 1080, label: "Landscape 16:9", note: "For X and YouTube" },
  vertical: { width: 1080, height: 1920, label: "Vertical 9:16", note: "For TikTok, Reels and Shorts" },
};

// How long the video runs: the race at the chosen speed (at most 2 minutes)
// plus the title card, the pauses and the closing card.
export function videoTiming(race, speed) {
  const raceS = Math.min(MAX_RACE_SECONDS, playSeconds(race) / speed);
  const total = T_TITLE + T_LEAD + raceS + T_HOLD + T_END;
  return { raceS, total, frames: Math.round(total * FPS) };
}

// football-data crest URL -> the same file on our own domain.
const CREST = /^https:\/\/crests\.football-data\.org\/([\w-]+\.(?:png|svg))$/; // keep in sync with scripts/mirror-crests.js
export const localCrest = (url) => {
  const m = CREST.exec(url || "");
  return m ? `/crests/${m[1]}` : null;
};

// Whether this browser can encode H.264 at the format's size.
export async function canMakeVideo(format) {
  if (typeof window === "undefined" || typeof window.VideoEncoder === "undefined" || typeof window.VideoFrame === "undefined") return false;
  try {
    const { canEncodeVideo, Quality } = await import("mediabunny");
    const { width, height } = VIDEO_FORMATS[format];
    return await canEncodeVideo("avc", { width, height, frameRate: FPS, quality: new Quality({ bitrate: BITRATE }) });
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Layouts (canvas pixels)

// Sizes are for phones: a landscape video on a phone is shown about 390 px
// wide (a fifth of 1920), a vertical one full screen (a third of 1080).
function layoutFor(format) {
  if (format === "vertical") {
    const W = 1080;
    const H = 1920;
    return {
      W,
      H,
      vertical: true,
      pad: 64,
      logo: { x: 64, y: 118, size: 92 },
      eyebrow: { x: 64, y: 318, size: 30, align: "left" },
      title: { x: 64, y: 402, size: 74, lineH: 84, maxW: W - 128, lines: 2, align: "left" },
      date: { x: 64, y: 566, size: 44, label: 26, labelX: W - 64 },
      chart: { left: 40, top: 606, right: 1040, bottom: 1236 },
      margin: { left: 84, leftPos: 52, right: 236, top: 16, bottom: 60 },
      heads: { r: 23, font: 30, gap: 40, values: false, maxRight: W - 16 },
      board: { left: 64, right: 960, top: 1276, bottom: 1790, row: 82, font: 38, crest: 52 }, // clear of TikTok's side buttons
      footer: { y: 1860, size: 32, center: true },
      tick: 28,
      line: 7,
      titleCard: { logo: 170, logoY: 290, wordmark: 106, wordmarkY: 510, eyebrow: 36, eyebrowY: 760, titleSize: 100, titleLineH: 112, titleLines: 3, titleY: 880, crest: 124, crestsY: 1330, rowGap: 230, perRow: 5, name: 32 },
      endCard: { eyebrow: 34, head: 88, rowMax: 104, rowsMaxH: 880, width: 952, font: 44, crest: 64, cta: 42, follow: 34 },
    };
  }
  const W = 1920;
  const H = 1080;
  return {
    W,
    H,
    vertical: false,
    pad: 72,
    logo: { x: 72, y: 40, size: 88 },
    eyebrow: { x: W - 72, y: 80, size: 28, align: "right" },
    title: { x: W - 72, y: 142, size: 58, lineH: 64, maxW: 1080, lines: 1, align: "right" },
    date: { x: 1392, y: 306, size: 42, season: 26, label: 24 },
    chart: { left: 60, top: 200, right: 1340, bottom: 970 },
    margin: { left: 96, leftPos: 60, right: 316, top: 16, bottom: 62 },
    heads: { r: 26, font: 34, gap: 46, values: true, maxRight: 1372 },
    board: { left: 1392, right: W - 72, top: 390, bottom: 960, row: 80, font: 34, crest: 48 },
    footer: { y: 1040, size: 34, center: false },
    tick: 30,
    line: 7,
    titleCard: { logo: 128, logoY: 96, wordmark: 80, wordmarkY: 256, eyebrow: 34, eyebrowY: 430, titleSize: 96, titleLineH: 106, titleLines: 2, titleY: 536, crest: 108, crestsY: 790, rowGap: 190, perRow: 10, name: 30 },
    endCard: { eyebrow: 30, head: 76, rowMax: 84, rowsMaxH: 560, width: 1080, font: 38, crest: 56, cta: 40, follow: 32 },
  };
}

// ---------------------------------------------------------------------------
// Drawing helpers

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const easeOut = (v) => 1 - (1 - clamp01(v)) ** 3;
const font = (weight, size) => `${weight} ${size}px ${FONT}`;

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Text with optional letter spacing (drawn a character at a time).
function text(ctx, str, x, y, { size, weight = 700, color = "#fff", align = "left", baseline = "alphabetic", spacing = 0 }) {
  ctx.font = font(weight, size);
  ctx.fillStyle = color;
  ctx.textBaseline = baseline;
  if (!spacing) {
    ctx.textAlign = align;
    ctx.fillText(str, x, y);
    return ctx.measureText(str).width;
  }
  const chars = [...str];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
  let cx = align === "right" ? x - total : align === "center" ? x - total / 2 : x;
  ctx.textAlign = "left";
  chars.forEach((c, i) => {
    ctx.fillText(c, cx, y);
    cx += widths[i] + spacing;
  });
  return total;
}

// The largest size (down to 60%) at which str fits maxW.
function fitSize(ctx, str, weight, size, maxW) {
  let s = size;
  ctx.font = font(weight, s);
  while (s > size * 0.6 && ctx.measureText(str).width > maxW) {
    s -= 1;
    ctx.font = font(weight, s);
  }
  return s;
}

// Word-wraps str into at most maxLines lines of maxW (the last is shrunk to fit).
function wrap(ctx, str, weight, size, maxW, maxLines) {
  ctx.font = font(weight, size);
  const lines = [];
  let line = "";
  for (const word of str.split(" ")) {
    const next = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(next).width > maxW && lines.length < maxLines - 1) {
      lines.push(line);
      line = word;
    } else line = next;
  }
  lines.push(line);
  return lines;
}

function ellipsize(ctx, str, maxW) {
  if (ctx.measureText(str).width <= maxW) return str;
  let s = str;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s.trimEnd()}…`;
}

function drawLogo(ctx, x, y, size) {
  const gap = size * 0.07;
  const cell = (size - gap * 2) / 3;
  LOGO_CELLS.forEach((row, r) =>
    row.forEach((fill, c) => {
      rr(ctx, x + c * (cell + gap), y + r * (cell + gap), cell, cell, cell * 0.22);
      ctx.fillStyle = fill;
      ctx.fill();
    })
  );
}

// "PREMIER LEAGUE" spread over the width of a gradient "SCORIGAMI", as in
// the site header. (x, top) is the block's top-left; returns its width.
function drawWordmark(ctx, x, top, size) {
  ctx.font = font(900, size);
  const w = ctx.measureText("SCORIGAMI").width;
  const small = size * 0.45;
  const topBase = top + small * 0.72;
  const base = topBase + size * 0.2 + size * 0.72;
  ctx.font = font(700, small);
  ctx.fillStyle = MUTED;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const items = [..."PREMIER LEAGUE"];
  const widths = items.map((c) => (c === " " ? 0 : ctx.measureText(c).width));
  const step = (w - widths.reduce((a, b) => a + b, 0)) / (items.length - 1);
  let cx = x;
  items.forEach((c, i) => {
    if (c !== " ") ctx.fillText(c, cx, topBase);
    cx += widths[i] + step;
  });
  const g = ctx.createLinearGradient(x, 0, x + w, 0);
  g.addColorStop(0, "#ffffff");
  g.addColorStop(0.38, "#c9f3ff");
  g.addColorStop(0.64, BRAND.cyan);
  g.addColorStop(1, BRAND.violet);
  ctx.font = font(900, size);
  ctx.fillStyle = g;
  ctx.fillText("SCORIGAMI", x, base);
  return w;
}
const wordmarkHeight = (size) => size * 0.45 * 0.72 + size * 0.2 + size * 0.72;

// Logo mark plus wordmark, vertically centred on the mark.
function drawLockup(ctx, x, y, logo) {
  drawLogo(ctx, x, y, logo);
  const size = logo * 0.66;
  return logo + logo * 0.24 + drawWordmark(ctx, x + logo * 0.24 + logo, y + (logo - wordmarkHeight(size)) / 2, size);
}

function drawXLogo(ctx, x, y, size, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 24, size / 24);
  ctx.fillStyle = color;
  ctx.fill(new Path2D(X_LOGO));
  ctx.restore();
}

// premierleaguescorigami.com and @pl_gami.
function drawFooter(ctx, L) {
  const { y, size, center } = L.footer;
  const color = "#e6e8ee";
  ctx.font = font(700, size);
  const handleW = ctx.measureText(HANDLE).width + size * 1.25;
  if (center) {
    const siteW = ctx.measureText(SITE).width;
    const sep = size * 1.4;
    const x0 = (L.W - (siteW + sep + handleW)) / 2;
    text(ctx, SITE, x0, y, { size, color });
    text(ctx, "·", x0 + siteW + sep / 2, y, { size, color: MUTED, align: "center" });
    drawXLogo(ctx, x0 + siteW + sep, y - size * 0.86, size * 0.95, color);
    text(ctx, HANDLE, x0 + siteW + sep + size * 1.25, y, { size, color });
    return;
  }
  text(ctx, SITE, L.pad, y, { size, color });
  drawXLogo(ctx, L.W - L.pad - handleW, y - size * 0.86, size * 0.95, color);
  text(ctx, HANDLE, L.W - L.pad, y, { size, color, align: "right" });
}

function drawProgress(ctx, L, p) {
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fillRect(0, L.H - 8, L.W, 8);
  const g = ctx.createLinearGradient(0, 0, L.W, 0);
  g.addColorStop(0, BRAND.cyan);
  g.addColorStop(1, BRAND.violet);
  ctx.fillStyle = g;
  ctx.fillRect(0, L.H - 8, L.W * clamp01(p), 8);
}

// Dark background with the site's soft violet and cyan glows (drawn once).
function makeBackground(L) {
  const c = document.createElement("canvas");
  c.width = L.W;
  c.height = L.H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = BRAND.bg;
  ctx.fillRect(0, 0, L.W, L.H);
  const R = Math.max(L.W, L.H);
  for (const [x, y, r, color] of [
    [L.W * 0.08, L.H * 0.04, R * 0.62, "rgba(111,93,245,0.16)"],
    [L.W * 0.96, L.H * 0.98, R * 0.55, "rgba(57,208,255,0.09)"],
  ]) {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, color);
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, L.W, L.H);
  }
  return c;
}

const initials = (name) =>
  name
    .replace(/^AFC /, "")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

// A club's crest fitted in a size×size box centred on (cx, cy), or its initials.
function drawCrest(ctx, st, i, cx, cy, size) {
  const img = st.crests.get(i);
  if (img) {
    const s = size / Math.max(img.naturalWidth, img.naturalHeight);
    const w = img.naturalWidth * s;
    const h = img.naturalHeight * s;
    ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
    return;
  }
  text(ctx, initials(clubInfo(st.teams[i].team).short), cx, cy + size * 0.02, { size: size * 0.42, weight: 800, align: "center", baseline: "middle" });
}

// ---------------------------------------------------------------------------
// Frames

function drawHeader(ctx, st, L) {
  drawLockup(ctx, L.logo.x, L.logo.y, L.logo.size);
  const e = L.eyebrow;
  text(ctx, st.eyebrow.toUpperCase(), e.x, e.y, { size: e.size, color: BRAND.cyan, align: e.align || "left", spacing: e.size * 0.14 });
  const t = L.title;
  if (t.lines === 1) {
    const size = fitSize(ctx, st.title, 900, t.size, t.maxW);
    text(ctx, st.title, t.x, t.y, { size, weight: 900, align: t.align });
  } else {
    wrap(ctx, st.title, 900, t.size, t.maxW, t.lines).forEach((line, k) => {
      const size = fitSize(ctx, line, 900, t.size, t.maxW);
      text(ctx, line, t.x, t.y + k * t.lineH, { size, weight: 900, align: t.align });
    });
  }
}

// The chart and leaderboard at playhead pos, with the value axis `dom`.
function drawRace(ctx, st, L, view, dom, rows) {
  const { race, series, metric, colors } = st;
  const c = L.chart;
  const M = L.margin;
  const left = metric.key === "pos" ? M.leftPos : M.left;
  const box = { left: c.left + left, top: c.top + M.top, width: c.right - c.left - left - M.right, height: c.bottom - c.top - M.top - M.bottom };
  const { x, y } = scales(view, box, dom, metric.invert);

  // Axes
  ctx.lineWidth = 2;
  for (const t of yTicksFor(metric, dom)) {
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.beginPath();
    ctx.moveTo(box.left, y(t));
    ctx.lineTo(box.left + box.width, y(t));
    ctx.stroke();
    text(ctx, metric.key === "pos" ? String(t) : metric.format(t), box.left - 16, y(t), { size: L.tick, weight: 500, color: MUTED, align: "right", baseline: "middle" });
  }
  for (const t of xTicksFor(race, view, box.width, L.tick / 11)) {
    text(ctx, t.label, x(t.w), c.bottom - 12, { size: L.tick, weight: 500, color: MUTED, align: "center" });
  }
  if (view.cur < view.n - 1) {
    ctx.save();
    ctx.setLineDash([8, 10]);
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(x(view.pos), box.top);
    ctx.lineTo(x(view.pos), box.top + box.height);
    ctx.stroke();
    ctx.restore();
  }

  // Lines, clipped to the plot
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.left - 4, 0, box.width + 8, L.H);
  ctx.clip();
  ctx.lineWidth = L.line;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  series.forEach((s, k) => {
    const d = linePath(s, view, x, y);
    if (!d) return;
    ctx.strokeStyle = colors[k];
    ctx.stroke(new Path2D(d));
  });
  ctx.restore();

  // Heads: crest in a ringed disc, name (and value) beside it
  const { r, font: fs, gap, values, maxRight } = L.heads;
  for (const h of placeHeads(series, view, x, y, gap, box.top + 10, box.top + box.height)) {
    const color = colors[h.k];
    if (Math.abs(h.ly - h.hy) > 2) {
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(h.hx + r + 3, h.hy);
      ctx.lineTo(h.hx + r + 12, h.ly);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.beginPath();
    ctx.arc(h.hx, h.hy, r + 4, 0, Math.PI * 2);
    ctx.fillStyle = BRAND.bg;
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    ctx.stroke();
    drawCrest(ctx, st, h.i, h.hx, h.hy, r * 2 - 4);
    const name = clubInfo(st.teams[h.i].team).short;
    const value = values ? metric.format(h.v) : "";
    const lx = h.hx + r + 16;
    const size = fitSize(ctx, value ? `${name}  ${value}` : name, 800, fs, maxRight - lx);
    const nameW = text(ctx, name, lx, h.ly, { size, baseline: "middle" });
    if (value) text(ctx, value, lx + nameW + size * 0.35, h.ly, { size, weight: 800, color, baseline: "middle" });
  }

  // Date and leaderboard
  const day = race.days[view.cur];
  const d = L.date;
  if (L.vertical) {
    text(ctx, fmtDay(day.date), d.x, d.y, { size: d.size });
    text(ctx, metric.label.toUpperCase(), d.labelX, d.y, { size: d.label, color: MUTED, align: "right", spacing: d.label * 0.14 });
  } else {
    text(ctx, `${seasonText(day.season)} SEASON`, d.x, d.y - d.size * 1.38, { size: d.season, color: BRAND.cyan, spacing: d.season * 0.14 });
    text(ctx, fmtDay(day.date), d.x, d.y, { size: d.size });
    text(ctx, metric.label.toUpperCase(), d.x, d.y + d.size * 1.05, { size: d.label, color: MUTED, spacing: d.label * 0.14 });
  }
  drawBoard(ctx, st, L.board, boardAt(series, metric, view.cur, view.frac), rows, false);
}

// Leaderboard rows (animated toward their rank when `rows` is given).
function drawBoard(ctx, st, b, board, rows, big) {
  const row = Math.min(b.row, (b.bottom - b.top) / Math.max(1, board.length));
  const h = row - Math.max(6, row * 0.12);
  const fs = Math.min(b.font, h * 0.5);
  board.forEach((r, rank) => {
    const target = b.top + rank * row;
    let yTop = target;
    if (rows) {
      const prev = rows.get(r.i);
      yTop = prev === undefined ? target : prev + (target - prev) * ROW_EASE;
      rows.set(r.i, yTop);
    }
    rr(ctx, b.left, yTop, b.right - b.left, h, Math.min(14, h * 0.2));
    if (big && rank === 0) {
      const g = ctx.createLinearGradient(b.left, 0, b.right, 0);
      g.addColorStop(0, "rgba(111,93,245,0.34)");
      g.addColorStop(1, "rgba(57,208,255,0.16)");
      ctx.fillStyle = g;
    } else ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.fill();
    ctx.fillStyle = st.colors[r.k];
    ctx.fillRect(b.left, yTop + h * 0.16, 6, h * 0.68);
    const cy = yTop + h / 2;
    const pad = fs * 0.75;
    text(ctx, String(rank + 1), b.left + pad, cy, { size: fs * 0.9, color: MUTED, baseline: "middle" });
    const crest = Math.min(b.crest, h * 0.78);
    const crestX = b.left + pad + fs * 1.45;
    drawCrest(ctx, st, r.i, crestX + crest / 2, cy, crest);
    const value = st.metric.format(r.v);
    const valueW = text(ctx, value, b.right - pad, cy, { size: fs * 1.08, weight: 800, align: "right", baseline: "middle" });
    const nameX = crestX + crest + fs * 0.5;
    const maxW = b.right - pad - valueW - fs * 0.6 - nameX;
    const info = clubInfo(st.teams[r.i].team);
    const size = fitSize(ctx, big ? info.name : info.short, 700, fs, maxW);
    ctx.font = font(700, size);
    text(ctx, ellipsize(ctx, big ? info.name : info.short, maxW), nameX, cy, { size, baseline: "middle" });
  });
}

// `fade` (0–1) scales everything, for the crossfade into the chart.
function drawTitleCard(ctx, st, L, t, fade) {
  const tc = L.titleCard;
  const appear = (delay) => fade * easeOut((t - delay) / 0.5);
  const cx = L.W / 2;
  const maxW = L.W - 2 * L.pad;

  ctx.globalAlpha = appear(0);
  drawLogo(ctx, cx - tc.logo / 2, tc.logoY, tc.logo);
  ctx.font = font(900, tc.wordmark);
  drawWordmark(ctx, cx - ctx.measureText("SCORIGAMI").width / 2, tc.wordmarkY, tc.wordmark);

  ctx.globalAlpha = appear(0.15);
  text(ctx, st.eyebrow.toUpperCase(), cx, tc.eyebrowY, { size: tc.eyebrow, color: BRAND.cyan, align: "center", spacing: tc.eyebrow * 0.16 });
  const lift = (1 - easeOut((t - 0.2) / 0.5)) * 28;
  ctx.globalAlpha = appear(0.2);
  wrap(ctx, st.title, 900, tc.titleSize, maxW, tc.titleLines).forEach((line, k) => {
    const size = fitSize(ctx, line, 900, tc.titleSize, maxW);
    text(ctx, line, cx, tc.titleY + k * tc.titleLineH + lift, { size, weight: 900, align: "center" });
  });

  // The clubs, in balanced rows, popping in one after another
  const n = st.selected.length;
  const rowsN = Math.ceil(n / tc.perRow);
  const per = Math.ceil(n / rowsN);
  const gapX = Math.min(tc.crest * 2.4, maxW / per);
  st.selected.forEach((i, k) => {
    const rowN = Math.floor(k / per);
    const inRow = Math.min(per, n - rowN * per);
    const col = k - rowN * per;
    const x = cx + (col - (inRow - 1) / 2) * gapX;
    const y = tc.crestsY + rowN * tc.rowGap;
    const a = easeOut((t - 0.35 - k * 0.07) / 0.5);
    ctx.globalAlpha = fade * a;
    const r = (tc.crest / 2) * (0.85 + 0.15 * a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.fill();
    ctx.strokeStyle = st.colors[k];
    ctx.lineWidth = 5;
    ctx.stroke();
    drawCrest(ctx, st, i, x, y, r * 1.3);
    const name = clubInfo(st.teams[i].team).short;
    const size = fitSize(ctx, name, 700, tc.name, gapX - 14);
    text(ctx, name, x, y + tc.crest / 2 + tc.name * 1.45, { size, color: "#e6e8ee", align: "center" });
  });
  ctx.globalAlpha = appear(0.3);
  drawFooter(ctx, L);
  ctx.globalAlpha = 1;
}

// The final standings and where to find more, fading in over the chart.
function drawEndCard(ctx, st, L, t) {
  const ec = L.endCard;
  ctx.globalAlpha = easeOut(t / FADE);
  ctx.drawImage(st.bg, 0, 0);
  const a = easeOut((t - 0.15) / FADE);
  const cx = L.W / 2;
  const board = boardAt(st.series, st.metric, st.n - 1, 0);
  const row = Math.min(ec.rowMax, ec.rowsMaxH / Math.max(1, board.length));

  // Stack top to bottom from 0, then centre the whole block.
  const eyebrowY = ec.eyebrow * 0.72;
  const headY = eyebrowY + ec.eyebrow * 0.9 + ec.head * 0.72;
  const rowsTop = headY + ec.head * 0.55;
  const ctaY = rowsTop + board.length * row + ec.cta * 1.5;
  const urlY = L.vertical ? ctaY + ec.cta * 1.3 : ctaY;
  const followY = urlY + ec.follow * 1.75;
  const off = (L.H - (followY + ec.follow * 0.3)) / 2 + (1 - a) * 24;

  ctx.globalAlpha = a;
  text(ctx, st.eyebrow.toUpperCase(), cx, off + eyebrowY, { size: ec.eyebrow, color: BRAND.cyan, align: "center", spacing: ec.eyebrow * 0.16 });
  text(ctx, st.endTitle, cx, off + headY, { size: ec.head, weight: 900, align: "center" });
  const top = off + rowsTop;
  drawBoard(ctx, st, { left: cx - ec.width / 2, right: cx + ec.width / 2, top, bottom: top + board.length * row, row, font: ec.font, crest: ec.crest }, board, null, true);

  // Call to action: the site, then the account
  ctx.globalAlpha = easeOut((t - 0.5) / FADE);
  const url = `${SITE}/race`;
  if (L.vertical) {
    text(ctx, "Replay any race at", cx, off + ctaY, { size: ec.cta, color: "#e6e8ee", align: "center" });
    text(ctx, url, cx, off + urlY, { size: fitSize(ctx, url, 800, ec.cta, L.W - 2 * L.pad), weight: 800, color: BRAND.cyan, align: "center" });
  } else {
    ctx.font = font(700, ec.cta);
    const lead = "Replay any race at ";
    const leadW = ctx.measureText(lead).width;
    ctx.font = font(800, ec.cta);
    const x0 = cx - (leadW + ctx.measureText(url).width) / 2;
    text(ctx, lead, x0, off + ctaY, { size: ec.cta, color: "#e6e8ee" });
    text(ctx, url, x0 + leadW, off + ctaY, { size: ec.cta, weight: 800, color: BRAND.cyan });
  }
  const follow = `Follow ${HANDLE} for every Premier League result`;
  text(ctx, follow, cx, off + followY, { size: fitSize(ctx, follow, 500, ec.follow, L.W - 2 * L.pad), weight: 500, color: MUTED, align: "center" });
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------

async function loadImage(src) {
  if (!src) return null;
  const img = new Image();
  img.src = src;
  try {
    await img.decode();
    return img.naturalWidth ? img : null;
  } catch {
    return null; // not mirrored yet: initials instead
  }
}

async function loadFonts() {
  if (!document.fonts?.load) return;
  await Promise.all(["500", "700", "800", "900"].map((w) => document.fonts.load(font(w, 40), "PREMIER LEAGUE 0123").catch(() => null)));
}

// A task boundary that background tabs don't throttle (unlike setTimeout),
// so the progress bar can paint.
const yieldToPage = () =>
  new Promise((resolve) => {
    const ch = new MessageChannel();
    ch.port1.onmessage = () => resolve();
    ch.port2.postMessage(null);
  });

// Renders the video and resolves to an MP4 Blob. `eyebrow` and `title`
// head the title card and every frame; `mode` and `speed` are the chart's.
// Rejects with an AbortError if `signal` aborts.
export async function makeRaceVideo({ race, teams, selected, colors, metric, mode, speed, format, eyebrow, title, onProgress, signal }) {
  const mb = await import("mediabunny");
  const L = layoutFor(format);
  const canvas = document.createElement("canvas");
  canvas.width = L.W;
  canvas.height = L.H;
  const ctx = canvas.getContext("2d", { alpha: false });
  const [, crests] = await Promise.all([loadFonts(), Promise.all(selected.map(async (i) => [i, await loadImage(localCrest(teams[i].image))]))]);
  const n = race.days.length;
  const st = {
    race,
    teams,
    selected,
    colors,
    metric,
    n,
    series: buildSeries(race, selected, metric),
    crests: new Map(crests.filter(([, img]) => img)),
    eyebrow,
    title,
    endTitle: "How it finished",
  };
  const seasons = seasonsIn(race);
  const bg = makeBackground(L);
  st.bg = bg;
  const { raceS, frames } = videoTiming(race, speed);
  const tStart = T_TITLE + T_LEAD;
  const tEnd = tStart + raceS;
  const tCard = tEnd + T_HOLD;

  const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: "in-memory" }), target: new mb.BufferTarget() });
  const source = new mb.CanvasSource(canvas, { codec: "avc", quality: new mb.Quality({ bitrate: BITRATE }), keyFrameInterval: 2 });
  output.addVideoTrack(source, { frameRate: FPS });
  await output.start();

  let dom = null;
  const rows = new Map();
  try {
    for (let f = 0; f < frames; f++) {
      if (signal?.aborted) throw new DOMException("Video cancelled", "AbortError");
      const t = f / FPS;
      ctx.drawImage(bg, 0, 0);
      if (t >= T_TITLE - FADE) {
        const pos = t < tStart ? 0 : t < tEnd ? ((n - 1) * (t - tStart)) / raceS : n - 1;
        const view = raceWindow(n, seasons, pos, mode);
        const target = targetDomain(st.series, metric, mode, view);
        dom = dom && t > tStart ? { min: dom.min + (target.min - dom.min) * DOMAIN_EASE, max: dom.max + (target.max - dom.max) * DOMAIN_EASE } : target;
        drawHeader(ctx, st, L);
        drawRace(ctx, st, L, view, dom, rows);
        drawFooter(ctx, L);
        if (t >= tCard) drawEndCard(ctx, st, L, t - tCard);
        drawProgress(ctx, L, (t - tStart) / raceS);
      }
      if (t < T_TITLE) {
        // The title card, fading out over the chart for its last FADE seconds
        const fade = t < T_TITLE - FADE ? 1 : 1 - (t - (T_TITLE - FADE)) / FADE;
        ctx.globalAlpha = fade;
        ctx.drawImage(bg, 0, 0);
        drawTitleCard(ctx, st, L, t, fade);
      }
      await source.add(t, 1 / FPS);
      if (f % 8 === 0) {
        onProgress?.(f / frames);
        await yieldToPage();
      }
    }
    await output.finalize();
  } catch (e) {
    await output.cancel().catch(() => null);
    throw e;
  }
  onProgress?.(1);
  return new Blob([output.target.buffer], { type: "video/mp4" });
}
